import {Action, Button, Icon, Label, TextArea, view} from '@sail/ui';
import {LightCard, MetaRow} from '@stripe-internal/conversations-ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage, useIntl, defineMessages} from 'react-intl';
import {cancel, info, warning} from '@sail/icons/react/Icon';
import {StatusChip} from './StatusChip';

const messages = defineMessages({
  title: {
    id: 'mse_inline_data.connected_account_chooser.title',
    description: 'Title displayed in the connected account support card.',
    defaultMessage: 'Connected account support',
  },
  textAreaLabel: {
    id: 'mse_inline_data.connected_account_chooser.text_area_label',
    description: 'Text area label for the connected account IDs.',
    defaultMessage: 'Account ID',
  },
  checkIdButton: {
    id: 'mse_inline_data.connected_account_chooser.check_id_button',
    description: 'Button to validate the connected account IDs.',
    defaultMessage: 'Check account IDs',
  },
  editButton: {
    id: 'mse_inline_data.connected_account_chooser.edit_button',
    description: 'Button to edit the account IDs.',
    defaultMessage: 'Edit account IDs',
  },
  continueButton: {
    id: 'mse_inline_data.connected_account_chooser.continue_button',
    description: 'Button to continue to flow.',
    defaultMessage: 'Continue',
  },
  validHeading: {
    id: 'mse_inline_data.connected_account_chooser.valid_heading',
    description: 'Heading for valid accounts',
    defaultMessage: 'Valid accounts',
  },
  validDescription: {
    id: 'mse_inline_data.connected_account_chooser.valid_description',
    description: 'Description for valid accounts',
    defaultMessage: 'Included in your case',
  },
  invalidHeading: {
    id: 'mse_inline_data.connected_account_chooser.invalid_heading',
    description: 'Heading for invalid accounts',
    defaultMessage: 'Invalid accounts',
  },
  invalidDescriptionEditing: {
    id: 'mse_inline_data.connected_account_chooser.invalid_description_edit',
    description: 'Description for invalid accounts when editing',
    defaultMessage: 'Will be ignored',
  },
  invalidDescriptionDone: {
    id: 'mse_inline_data.connected_account_chooser.invalid_description_done',
    description: 'Description for invalid accounts after finished',
    defaultMessage: 'These were ignored',
  },
  noAccountsWarning: {
    id: 'mse_inline_data.connected_account_chooser.no_accounts_warning',
    description: 'Warning when no valid accounts found',
    defaultMessage: 'No accounts found.',
  },
  noAccountsDescription: {
    id: 'mse_inline_data.connected_account_chooser.no_accounts_description',
    description: 'Warning description when no valid accounts found',
    defaultMessage:
      'Please ensure the connected account IDs are in this format: "acct_1ABC2D3eFgh"',
  },
  accountsLimitWarning: {
    id: 'mse_inline_data.connected_account_chooser.accounts_limit_warning',
    description:
      'Warning when user provides more than 100 valid connected accounts',
    defaultMessage: "We've selected the first 100 accounts you entered.",
  },
  skippedLogMessage: {
    id: 'mse_inline_data.connected_account_chooser.skipped_log_message',
    description: 'Log message when user skips connected account ID entry',
    defaultMessage: 'Connected account ID entry skipped',
  },
});

export type ValueProps = {
  accounts?: string[];
  valid_connected_accounts?: string[];
  invalid_connected_accounts?: string[];
};

export const ConnectedAccountChooser = (
  props: InlineDatumReactComponentProps,
) => {
  const {datum, action, onDone, isDone} = props;
  const value = datum.value as ValueProps;
  const {
    accounts,
    valid_connected_accounts: validConnectedAccounts,
    invalid_connected_accounts: invalidConnectedAccounts,
  } = value;

  const intl = useIntl();
  const analytics = useAnalytics();

  const [allAccountsInput, setAllAccountsInput] = useState<string>();
  const [allAccounts, setAllAccounts] = useState<string[]>();
  const [validAccounts, setValidAccounts] = useState<string[]>();
  const [invalidAccounts, setInvalidAccounts] = useState<string[]>();
  const [isFinished, setIsFinished] = useState<boolean>();
  const [isSkipped, setIsSkipped] = useState<boolean>();

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const hasReachedAccountsLimit =
    validAccounts &&
    validAccounts.length >= 100 &&
    allAccounts &&
    allAccounts.length > 100;

  const pageState = useMemo(
    () => ({
      isEditing,
      hasValidAccounts: (validConnectedAccounts?.length || 0) > 0,
      hasInvalidAccounts: (invalidConnectedAccounts?.length || 0) > 0,
      hasInput: (allAccountsInput?.length || 0) > 0,
    }),
    [
      allAccountsInput?.length,
      invalidConnectedAccounts?.length,
      isEditing,
      validConnectedAccounts?.length,
    ],
  );

  // set local states so that UI is re-rendered upon state updates
  useEffect(() => {
    setAllAccounts(accounts || []);
    setAllAccountsInput(accounts?.join(',\n'));
    setValidAccounts(validConnectedAccounts || []);
    setInvalidAccounts(invalidConnectedAccounts || []);
    setIsFinished(isDone || false);

    // if accounts are not set and isDone is true, then we know the user skipped
    setIsSkipped(isDone && (!accounts || accounts.length === 0));
  }, [accounts, invalidConnectedAccounts, isDone, validConnectedAccounts]);

  useEffect(() => {
    if (isDone) {
      return;
    }

    // if backend inline data returns a list of validated accounts, then we know
    // it has already checked validity and we are not in edit mode.
    setIsEditing(!validConnectedAccounts && !invalidConnectedAccounts);
  }, [invalidConnectedAccounts, isDone, validConnectedAccounts]);

  const checkIdsPressed = () => {
    action({
      accounts: allAccounts,
    });
    setIsEditing(false);
    analytics.action('connected_account_chooser_check_id_click', pageState);
  };

  const editIdsPressed = () => {
    setIsEditing(true);
    analytics.action('connected_account_chooser_edit_id_click', pageState);
  };

  const continuePressed = useCallback(() => {
    onDone({
      type: 'success',
      message: validConnectedAccounts?.join(','),
      extra: {
        outcome: validConnectedAccounts?.join(','),
        value,
      },
    });
    // immediately set UI to be done to avoid the delay for isDone to become true
    setIsFinished(true);
    analytics.action('connected_account_chooser_continue_click', pageState);
  }, [analytics, onDone, pageState, validConnectedAccounts, value]);

  const cancelPressed = useCallback(() => {
    onDone({
      type: 'success',
      message: '',
      extra: {
        outcome: '',
      },
    });
    setIsSkipped(true);
    setIsFinished(true);
    analytics.action('connected_account_chooser_cancel_click', pageState);
  }, [analytics, onDone, pageState]);

  const removeValidAccount = useCallback(
    (account: string) => {
      if (validAccounts) {
        const i = validAccounts.indexOf(account);
        if (i > -1) {
          validAccounts.splice(i, 1);
          setValidAccounts(validAccounts);
          setAllAccounts(validAccounts.concat(invalidAccounts || []));
        }
      }
    },
    [invalidAccounts, validAccounts],
  );

  if (isSkipped) {
    return (
      <view.div
        css={{margin: 'medium'}}
        data-testid="skip-connected-account-meta-row"
      >
        <MetaRow>{intl.formatMessage(messages.skippedLogMessage)}</MetaRow>
      </view.div>
    );
  }

  return (
    <view.div css={{marginTop: 'small'}}>
      <LightCard isOuter>
        <view.div css={{gap: 'medium', stack: 'y'}}>
          <view.div css={{margin: 0, font: 'heading.small', display: 'flex'}}>
            {intl.formatMessage(messages.title)}

            {!isFinished ? (
              <Action
                css={{marginLeft: 'auto'}}
                onPress={cancelPressed}
                data-testid="cancel-btn"
              >
                <Icon icon={cancel} size="small" />
              </Action>
            ) : null}
          </view.div>

          {isEditing ? (
            <>
              <TextArea
                data-testid="connected-accounts-textarea"
                placeholder="acct_123456789, acct_abcd1234, etc"
                label={intl.formatMessage(messages.textAreaLabel)}
                rows={Math.min(validAccounts?.length || 3, 8)}
                value={allAccountsInput}
                onChange={(e) => {
                  setAllAccountsInput(e.target.value);
                  setAllAccounts(
                    e.target.value.split(/[\s,]+/).filter((s) => s.length > 0),
                  );
                }}
              />
              <Button
                css={{width: 'fill'}}
                data-testid="check-account-ids-btn"
                type="primary"
                onPress={checkIdsPressed}
              >
                <FormattedMessage {...messages.checkIdButton} />
              </Button>
            </>
          ) : (
            <>
              {validAccounts && validAccounts.length > 0 ? (
                <view.div css={{stack: 'y'}}>
                  <Label>{intl.formatMessage(messages.validHeading)}</Label>
                  <view.div css={{font: 'heading.xsmall.subdued'}}>
                    {intl.formatMessage(messages.validDescription)}
                  </view.div>
                  {hasReachedAccountsLimit ? (
                    <view.div
                      css={{
                        stack: 'x',
                        gap: 'xsmall',
                        textColor: 'subdued',
                        alignY: 'center',
                        font: 'label.small',
                      }}
                    >
                      <Icon icon={info} size="xsmall" />
                      {intl.formatMessage(messages.accountsLimitWarning)}
                    </view.div>
                  ) : null}
                  <view.div
                    css={{gap: 'small', stack: 'y', marginTop: 'small'}}
                    data-testid="valid-accounts-list"
                  >
                    {validAccounts?.map((account) => (
                      <StatusChip
                        key={account}
                        label={account}
                        status="valid"
                        onClose={
                          isFinished
                            ? undefined
                            : () => removeValidAccount(account)
                        }
                      />
                    ))}
                  </view.div>
                </view.div>
              ) : (
                <>
                  <view.div
                    data-testid="no-valid-accounts-warning-section"
                    css={{
                      stack: 'x',
                      gap: 'xsmall',
                      color: 'action.destructive',
                      alignY: 'center',
                      font: 'label.small',
                    }}
                  >
                    <Icon icon={warning} size="xsmall" />
                    <view.div>
                      {intl.formatMessage(messages.noAccountsWarning)}
                    </view.div>
                  </view.div>

                  <view.div css={{font: 'label.small', textColor: 'subdued'}}>
                    {intl.formatMessage(messages.noAccountsDescription)}
                  </view.div>
                </>
              )}

              {invalidAccounts && invalidAccounts.length > 0 ? (
                <view.div
                  css={{stack: 'y'}}
                  data-testid="invalid-accounts-section"
                >
                  <Label>{intl.formatMessage(messages.invalidHeading)}</Label>
                  <view.div css={{font: 'heading.xsmall.subdued'}}>
                    {intl.formatMessage(
                      isFinished
                        ? messages.invalidDescriptionDone
                        : messages.invalidDescriptionEditing,
                    )}
                  </view.div>
                  <view.div
                    css={{gap: 'small', stack: 'y', marginTop: 'small'}}
                    data-testid="invalid-accounts-list"
                  >
                    {invalidAccounts?.map((account) => (
                      <StatusChip
                        key={account}
                        label={account}
                        status="invalid"
                      />
                    ))}
                  </view.div>
                </view.div>
              ) : null}

              {!isFinished && (
                <>
                  <Button
                    css={{width: 'fill'}}
                    data-testid="edit-account-ids-btn"
                    onPress={editIdsPressed}
                  >
                    <FormattedMessage {...messages.editButton} />
                  </Button>
                  <Button
                    css={{width: 'fill'}}
                    data-testid="continue-btn"
                    type="primary"
                    onPress={continuePressed}
                    disabled={!validAccounts || validAccounts.length === 0}
                  >
                    <FormattedMessage {...messages.continueButton} />
                  </Button>
                </>
              )}
            </>
          )}
        </view.div>
      </LightCard>
    </view.div>
  );
};
